<template>
    <div class="mb-6 select-none border-b-2 border-theme-divider-soft p-4 text-theme-text [container-type:inline-size]">
        <!-- The form takes the row over; the filters come back once it closes. -->
        <HighlighterAddForm
            v-if="isAddingHighlighter"
            variant="compact"
            @added="closeHighlighterForm"
            @cancel="closeHighlighterForm"
        />
        <div v-else class="export-toolbar flex items-center justify-between gap-2">
            <div class="flex flex-wrap items-center gap-2">
                <template v-for="(item, index) in filteredHighlighters">
                    <span v-if="item.divider" :key="'divider-' + index" class="mx-0.5 text-theme-border">|</span>
                    <div v-else :key="item.slug" v-tooltip.top-start="colorTooltip(item)" class="flex items-center">
                        <input
                            :id="`${colorInputIdPrefix}-${item.slug}`"
                            :checked="checkedColors.includes(item.slug)"
                            :value="item.slug"
                            :aria-label="colorTooltip(item)"
                            type="checkbox"
                            class="peer sr-only"
                            @change="toggleColor(item.slug)"
                        />
                        <ColorSwatch
                            :for-id="`${colorInputIdPrefix}-${item.slug}`"
                            :color="item.color"
                            :is-custom="item.custom"
                            :is-selected="checkedColors.includes(item.slug)"
                            size="filter"
                            class="tap-target peer-focus-visible:ring-2 peer-focus-visible:ring-theme-accent peer-focus-visible:ring-offset-2"
                        >
                            <template v-if="item.author" #overlay>
                                <!-- eslint-disable-next-line volpe/no-hardcoded-color-class -- on the highlight's own color, which no theme sets -->
                                <svg-icon class="text-white" icon="shared-group-24px" width="10" height="10" />
                            </template>
                        </ColorSwatch>
                    </div>
                </template>

                <!-- Same footprint as a chip, so it reads as one more color in the row. -->
                <button
                    v-if="canAddMoreHighlighters"
                    ref="addHighlighterButton"
                    v-tooltip.top="localizations[lang].newHighlighter"
                    type="button"
                    data-role="add-highlighter"
                    class="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border border-dashed border-theme-toolbar-icon bg-transparent text-theme-toolbar-icon opacity-60 transition-opacity tap-target hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                    :aria-label="localizations[lang].newHighlighter"
                    @click="isAddingHighlighter = true"
                >
                    <svg-icon icon="plus-bold" width="10" height="10" />
                </button>
            </div>

            <div class="flex flex-shrink-0 gap-1.5">
                <slot name="actions" />
                <PillIconButton
                    v-tooltip.top="shareTooltip"
                    icon="user-plus-24px"
                    variant="accent"
                    :icon-size="18"
                    :disabled="usedColorSlugs.length === 0 || isOffline"
                    :aria-label="shareTooltip"
                    @click="openShareCollectionModal"
                />
                <PillIconButton
                    v-if="copyContentToClipboard.enabled && !disableExport"
                    v-tooltip.top="exportTooltip"
                    icon="export-notes"
                    variant="neutral"
                    :icon-size="18"
                    :disabled="highlightIsEmpty"
                    :aria-label="exportTooltip"
                    @click="exportAction"
                />
                <slot name="actions-end" />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import downloadFile from '../../../js/utils/downloadFile';
import { buildNotebookTextExport } from '../../../js/utils/notebookTextExport';
import { uiAPI } from '../../../../src/js/communication';
import ColorSwatch from '../../Components/ColorSwatch.vue';
import PillIconButton from '../../Components/PillIconButton.vue';
import HighlighterAddForm from '../../HighlighterColorPicker/HighlighterAddForm.vue';

export default {
    components: {
        ColorSwatch,
        PillIconButton,
        HighlighterAddForm,
    },
    props: {
        highlights: {
            type: Array,
            default: () => [],
        },
        disableExport: {
            type: Boolean,
            default: false,
        },
        checkedColors: {
            type: Array,
            required: true,
        },
    },
    emits: ['checked-colors'],
    data() {
        return {
            isAddingHighlighter: false,
        };
    },
    computed: {
        /**
         * Prefijo de id único por instancia del toolbar, para que la asociación
         * checkbox/label `for` no colisione con otro ExportNotesOrHighlight montado
         * a la vez (tabs del notebook + panel de flashcards).
         * @returns {string}
         */
        colorInputIdPrefix() {
            return `filterColor-${this._uid}`;
        },
        ...mapState([
            'title',
            'uiHidden',
            'ui',
            'localizations',
            'lang',
            'issue',
            'embeddedInMobileApp',
            'copyContentToClipboard',
            'sharedColors',
            'isOffline',
        ]),
        ...mapGetters([
            'isEpub',
            'onlyMobileVisible',
            'isPdf',
            'allHighlighters',
            'usedColorSlugs',
            'canAddMoreHighlighters',
        ]),
        highlightIsEmpty() {
            return this.highlights.length === 0;
        },
        /**
         * Returns the color swatches to display.
         * Includes a divider marker between own and shared colors when both exist.
         */
        filteredHighlighters() {
            if (this.sharedColors.length === 0) return this.allHighlighters;
            return [...this.allHighlighters, { divider: true }, ...this.sharedColors];
        },
        shareTooltip() {
            return this.localizations[this.lang].sharing?.shareMyHighlights || 'Share';
        },
        exportTooltip() {
            return this.localizations[this.lang].sharing?.exportHighlights || this.localizations[this.lang].export;
        },
    },
    methods: {
        ...mapMutations(['openShareCollectionModal']),
        /**
         * Closes the form and hands focus back to the chip it was opened from. That chip
         * leaves the row once the limit is reached, so the last color filter takes over.
         */
        closeHighlighterForm() {
            this.isAddingHighlighter = false;
            this.$nextTick(() => {
                if (this.$refs.addHighlighterButton) {
                    this.$refs.addHighlighterButton.focus();
                    return;
                }
                const colorFilters = this.$el.querySelectorAll('input[type="checkbox"]');
                colorFilters[colorFilters.length - 1]?.focus();
            });
        },
        /**
         * Returns the tooltip text for a color swatch.
         * Shows owner email for shared colors.
         * @param {Object} item - Color item
         * @returns {string}
         */
        colorTooltip(item) {
            const colorLabel = this.localizations[this.lang][item.label] || item.label;
            if (item.author?.email) return `${colorLabel} (${item.author.email})`;
            return colorLabel;
        },
        /**
         * Toggles a color slug in the checked list and emits the updated array.
         * @param {string} slug - The color slug to toggle
         */
        toggleColor(slug) {
            const updatedCheckedColors = this.checkedColors.includes(slug)
                ? this.checkedColors.filter(s => s !== slug)
                : [...this.checkedColors, slug];
            this.$emit('checked-colors', updatedCheckedColors);
        },
        /**
         * Hands the notebook's text export to the mobile app, or downloads it in the browser.
         */
        exportAction() {
            if (this.highlightIsEmpty) return;

            const { text, fileName } = buildNotebookTextExport(
                this.highlights,
                this.localizations[this.lang],
                this.title
            );

            if (this.embeddedInMobileApp) {
                uiAPI.exportText({ text, fileName });
                return;
            }

            downloadFile(text, 'text/plain', fileName);
        },
    },
};
</script>

<style scoped>
/* The panel is user-resizable, so stack on the toolbar's own width, not the viewport. */
@container (max-width: 480px) {
    .export-toolbar {
        @apply flex-col items-stretch justify-start;
    }
}
</style>
